// Pure helpers for the anonymous share view (sharing v2).
//
// Extracted from <shared-deck-page> so the stateful bits — the stable per-browser
// viewer id, the per-session "have I already recorded this slide?" dedupe, and the
// owner-facing passcode generator — unit-test without a DOM or a live network.
//
// All helpers are deliberately small and side-effect-light. The viewer-id and
// name helpers take a Storage so tests can pass a fake; the production callers
// pass window.localStorage. British spelling throughout. No emojis.

/** localStorage keys. Kept here so the page and the tests reference the same
 *  strings rather than duplicating literals. */
export const VIEWER_ID_KEY = 'deck:viewerId';
export const VIEWER_NAME_KEY = 'deck:viewerName';

/** Minimal Storage surface — matches the slice of the Web Storage API we use,
 *  so a plain object-backed fake satisfies it in tests. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Generate a UUID. Uses crypto.randomUUID when available (every browser the
 *  SPA targets has it), with a deterministic-enough fallback so the helper
 *  never throws in a barebones environment. */
function randomId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback — not cryptographically strong, only used if randomUUID is absent.
  return 'v-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Get the stable viewer id for this browser, creating + persisting one on first
 * call. The same browser keeps the same id across visits so per-viewer analytics
 * can group their opens. Defensive: if storage throws (private mode), returns a
 * fresh ephemeral id rather than crashing the view.
 */
export function getOrCreateViewerId(storage: StorageLike): string {
  try {
    const existing = storage.getItem(VIEWER_ID_KEY);
    if (existing && existing.trim()) return existing;
    const id = randomId();
    storage.setItem(VIEWER_ID_KEY, id);
    return id;
  } catch {
    return randomId();
  }
}

/** Read the saved viewer name, or null when none has been set. Empty / blank
 *  values are treated as "no name". */
export function getSavedViewerName(storage: StorageLike): string | null {
  try {
    const raw = storage.getItem(VIEWER_NAME_KEY);
    if (raw === null) return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/** Persist a viewer name. Trims; a blank name is a no-op (we never store an
 *  empty string, so getSavedViewerName stays a clean null check). */
export function saveViewerName(storage: StorageLike, name: string): void {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return;
  try {
    storage.setItem(VIEWER_NAME_KEY, trimmed);
  } catch {
    /* private mode — name simply won't persist */
  }
}

/**
 * Per-session dedupe for slide-view events. The share view records a 'slide'
 * event the FIRST time each slide index is seen this session, so per-slide
 * counts are unique-per-session rather than inflated by back-and-forth nav.
 *
 * `markSlideSeen` returns true when the index was NEW (caller should record),
 * false when already seen (caller should skip). Non-finite / negative indices
 * are rejected as never-new (returns false) so a malformed message can't
 * trigger a spurious event.
 */
export function markSlideSeen(seen: Set<number>, index: number): boolean {
  if (typeof index !== 'number' || !Number.isFinite(index) || index < 0) return false;
  const i = Math.trunc(index);
  if (seen.has(i)) return false;
  seen.add(i);
  return true;
}

/** Unambiguous alphabet for generated passcodes — no 0/O, 1/I/L, etc., so a
 *  recipient typing it from a chat message doesn't misread a character. */
export const PASSCODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/**
 * Generate a human-friendly passcode. ~9 characters drawn from the unambiguous
 * alphabet, grouped in threes with a dash for legibility (e.g. "K7M-PQ4-T9X").
 * Uses crypto.getRandomValues for uniform, unbiased selection (modulo-bias-free
 * via rejection on the alphabet length, which divides evenly enough that we use
 * the simple masked approach: draw bytes, reject those >= floor(256/len)*len).
 *
 * `groups` × `groupSize` controls length; defaults give 9 chars in 3 groups.
 */
export function generatePasscode(groups = 3, groupSize = 3): string {
  const len = PASSCODE_ALPHABET.length;
  const total = Math.max(1, groups) * Math.max(1, groupSize);
  const out: string[] = [];
  const c = (globalThis as { crypto?: Crypto }).crypto;
  // Rejection threshold to avoid modulo bias.
  const limit = Math.floor(256 / len) * len;
  while (out.length < total) {
    let byte: number;
    if (c && typeof c.getRandomValues === 'function') {
      const buf = new Uint8Array(1);
      c.getRandomValues(buf);
      byte = buf[0];
    } else {
      byte = Math.floor(Math.random() * 256);
    }
    if (byte >= limit) continue; // reject to keep the distribution uniform
    out.push(PASSCODE_ALPHABET[byte % len]);
  }
  // Group into chunks joined by '-'.
  const chunks: string[] = [];
  for (let i = 0; i < out.length; i += groupSize) {
    chunks.push(out.slice(i, i + groupSize).join(''));
  }
  return chunks.join('-');
}

/** The display name for a viewer: the given name if present, else 'Anonymous'.
 *  Centralised so the page and the analytics panel agree. */
export function displayName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  return trimmed.length > 0 ? trimmed : 'Anonymous';
}
